import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Container,
  Grid,
  IconButton,
  TextField,
  Typography,
  Backdrop,
  Box,
  Button,
  FormControlLabel,
  Modal,
  Switch,
  Chip,
  useTheme,
} from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import {
  BCPSquarePlus,
  BCPXmark,
  BCPFilter
} from "../../../components/icons/bcp/BCPIcons";

import {
  handlePagingAll,
  getGameSystems,
} from "../../../components/api/APIUtilities";
import ListResponsive from "../../../components/other/ListResponsive";
import { AdminTabs } from "../Admin";
import GameSystemPicker from "../../../components/utilities/gamesystempickers/GameSystemPicker";
import ImageUploader, {
  uploadBas64ToS3,
} from "../../../components/utilities/ImageUploader";
import GenericPicker from "../../../components/utilities/GenericPicker";
import { postData } from "../../../components/utilities/UtilityFunctions";
import OptionsMenu from "../../../components/layout/OptionsMenu";


const sortFactions = (a, b) => {
  var parentNameA = (
    a?.ITCFaction?.name ? a.ITCFaction?.name : a?.name
  )?.toLowerCase();
  var parentNameB = (
    b?.ITCFaction?.name ? b.ITCFaction?.name : b?.name
  )?.toLowerCase();
  if (parentNameA > parentNameB) return 1;
  if (parentNameA < parentNameB) return -1;

  let nameA = a?.name?.toLowerCase();
  let nameB = b?.name?.toLowerCase();

  if (parentNameA === nameA) return -1;
  if (parentNameB === nameB) return 1;

  if (nameA > nameB) return 1;
  if (nameA < nameB) return -1;
  return 0;
};

export default function AdminArmies(props) {
  const { activeTab, handleTabChange, authContext } = props;

  const theme = useTheme();

  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subLoading, setSubLoading] = useState(true);

  const [gameSystems, setGameSystems] = useState(null);
  const [gameSystem, setGameSystem] = useState(null);

  const [factions, setFactions] = useState(null);
  const [faction, setFaction] = useState(null);
  const [factionFilter, setFactionFilter] = useState("no-filter");
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const go = async () => {
      var apiGameSystems = await getGameSystems();
      if (
        !authContext?.authState?.identity?.isAdmin &&
        authContext?.authState?.identity?.adminGameSystems?.length > 0
      ) {
        apiGameSystems = apiGameSystems.filter((el) =>
          authContext.authState.identity.adminGameSystems.includes(el.id)
        );
      }
      setGameSystems(apiGameSystems);
      setLoading(false);
    };
    go(); // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const go = async () => {
      setSubLoading(true);
      setFactions(null);
      setFactionFilter("no-filter");
      if (gameSystem) {
        var apiArmies = await handlePagingAll("/armies", {
          gameType: gameSystem.code,
          "expand[]": ["imageFile", "ITCFaction"],
        });

        apiArmies.sort(sortFactions);

        setFactions(apiArmies);
      }

      setSubLoading(false);
    };
    go();
  }, [gameSystem]);

  const filteredFactions = React.useMemo(() => {
    if (!factions) return null;
    if (factionFilter === "no-filter") return factions;
    if (factionFilter === "active") return factions.filter((f) => f.active);
    if (factionFilter === "inactive") return factions.filter((f) => !f.active);
    return factions;
  }, [factions, factionFilter]);

  const handleToggleActive = async (row) => {
    const newActive = !row.active;
    setFactions((prev) =>
      prev.map((f) => (f.id === row.id ? { ...f, active: newActive } : f))
    );
    try {
      await postData(`/armies/${row.id}`, {
        body: { set: { active: newActive } },
      });
    } catch (err) {
      setFactions((prev) =>
        prev.map((f) => (f.id === row.id ? { ...f, active: row.active } : f))
      );
      postNotification("error", err);
    }
  };

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "background.secondary",
          }}
        >
          <Typography variant="h3">Admin Settings</Typography>

          <AdminTabs
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </Grid>
        {gameSystems && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary",
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <GameSystemPicker
                  name="gameType"
                  value={gameSystem?.code || ""}
                  handleChange={(e) => {
                    if (e.target.value) {
                      gameSystems.forEach((gs) => {
                        if (gs.code === e.target.value) {
                          setGameSystem(gs);
                          return true;
                        }
                      });
                    }
                  }}
                  gameSystems={gameSystems}
                />
              </Grid>
            </Grid>
          </Grid>
        )}

        {gameSystem && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary",
            }}
          >
            <ListResponsive
              originalList={subLoading ? null : filteredFactions}
              doSearch
              doPages
              initialRowsPerPage={24}
              actionButton={[
                {
                  props: {
                    variant: "contained",
                    color: "primary",
                    onClick: (e) => {
                      setAnchorEl(e.currentTarget);
                    },
                    startIcon: <BCPFilter />
                  },
                  label: "Status"
                },
                {
                  props: {
                    variant: "contained",
                    color: "primary",
                    onClick: () => {
                      setFaction({});
                    },
                    startIcon: <BCPSquarePlus />,
                  },
                  label: `Add ${gameSystem?.factionLabel || "Faction"}`,
                },
              ]}
              searchProps={{
                label: `Search ${gameSystem?.factionLabel || "Faction"}s`,
                performSearch: (originalList, searchText) => {
                  if (!searchText || searchText === "") {
                    return originalList;
                  } else {
                    var filteredList = originalList.filter(function (el) {
                      if (el?.name?.toLowerCase().includes(searchText))
                        return true;
                      if (
                        el?.ITCFaction?.name?.toLowerCase().includes(searchText)
                      )
                        return true;

                      return false;
                    });
                    return filteredList;
                  }
                },
              }}
              rowProps={{
                mapFunction: (row, index) => {
                  return (
                    <Grid
                      key={index}
                      item
                      xs={12}
                      onClick={(e) => {
                        setFaction(row);
                      }}
                      sx={{
                        p: 1,
                        mb: 1,
                        border: "1px solid rgba(0, 0, 0, 0.12)",
                        borderRadius: 1,
                        boxShadow: 1,
                        backgroundColor:
                          index % 2 !== 0
                            ? theme.palette.action.hover
                            : "transparent",
                        "&:hover": {
                          cursor: "pointer",
                        },
                      }}
                    >
                      <Grid container alignItems="center" wrap="nowrap" spacing={1}>
                        <Grid item xs>
                          <Typography variant="body1">
                            {!row?.ITCFactionId || row?.ITCFactionId === row?.id
                              ? row?.name
                              : `${row?.ITCFaction?.name} - ${row?.name}`}
                          </Typography>
                        </Grid>
                        <Grid item sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Chip
                            label="Parent"
                            color={row.itcFaction ? "primary" : "default"}
                            variant={row.itcFaction ? "filled" : "outlined"}
                          />
                          <Chip
                            label="Circuit"
                            color={row.leagueFaction ? "secondary" : "default"}
                            variant={row.leagueFaction ? "filled" : "outlined"}
                          />
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Typography variant="body2">Active</Typography>
                            <Switch
                              checked={Boolean(row.active)}
                              onChange={() => handleToggleActive(row)}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                },
              }}
            />
          </Grid>
        )}
      </Grid>

      <OptionsMenu
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        items={[
          {
            name: "faction-filter-all",
            label: "All",
            selected: factionFilter === "no-filter",
            onClick: () => {
              setFactionFilter("no-filter");
              setAnchorEl(null);
            }
          },
          {
            name: "faction-filter-active",
            label: "Active",
            selected: factionFilter === "active",
            onClick: () => {
              setFactionFilter("active");
              setAnchorEl(null);
            }
          },
          {
            name: "faction-filter-inactive",
            label: "Inactive",
            selected: factionFilter === "inactive",
            onClick: () => {
              setFactionFilter("inactive");
              setAnchorEl(null);
            }
          }
        ]}
        name="faction-filter-menu"
      />

      <FactionModal
        gameSystem={gameSystem}
        faction={faction}
        setFaction={setFaction}
        factions={factions}
        setFactions={setFactions}
        setProcessing={setProcessing}
      />

      {Boolean(processing || loading) && <ModalLoadingFull />}
    </Container>
  );
}

function FactionModal(props) {
  const {
    gameSystem,
    faction,
    setFaction,
    factions,
    setFactions,
    setProcessing,
  } = props;

  const theme = useTheme();

  return (
    <Modal
      disableAutoFocus
      open={Boolean(faction)}
      onClose={() => {
        setFaction(null);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      {faction ? (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setFaction(null);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs>
              <Typography variant="h4" component="h2">
                {faction.id
                  ? `Update ${gameSystem?.factionLabel || "Faction"}`
                  : `Create ${gameSystem?.factionLabel || "Faction"}`}
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              id: faction?.id || "",
              name: faction?.name || "",
              active: faction?.active !== undefined ? faction.active : true,
              itcFaction:
                faction?.itcFaction !== undefined
                  ? faction.itcFaction
                  : faction?.ITCFactionId === faction?.id,
              leagueFaction:
                faction?.leagueFaction !== undefined
                  ? faction.leagueFaction
                  : true,
              rankingSortOrder: faction?.rankingSortOrder || undefined,

              ITCFaction: faction?.ITCFaction || undefined,
              ITCFactionId: faction?.ITCFactionId || "",

              searchName: faction?.searchName || [],
              searchNameList:
                faction?.searchName?.length > 0
                  ? faction.searchName.join(",")
                  : "",
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              name: Yup.string()
                .min(3, "Minimum 3 Characters!")
                .max(255, "Maximum 255 Characters!")
                .required("Required"),
              // TODO: Add validation schema for new values
            })}
            onSubmit={async (values) => {
              var apiData;
              var newImageFile;
              setProcessing(true);
              try {
                var sanitizedValues = {
                  ...values,
                };
                delete sanitizedValues.ITCFaction;

                if (sanitizedValues.id?.trim() === "") {
                  delete sanitizedValues.id;
                }
                if (sanitizedValues.ITCFactionId?.trim() === "") {
                  delete sanitizedValues.ITCFactionId;
                }
                if (gameSystem) {
                  sanitizedValues.gameSystemId = gameSystem.id;
                  sanitizedValues.gameType = gameSystem.code;
                }

                if (sanitizedValues.factionImage) {
                  // upload image to s3
                  newImageFile = await uploadBas64ToS3(
                    sanitizedValues.factionImage
                  );
                  delete sanitizedValues.factionImage;
                  if (newImageFile.fileId) {
                    sanitizedValues.imageFileId = newImageFile.fileId;
                  }
                }

                if (sanitizedValues.id) {
                  var body = {
                    set: sanitizedValues,
                  };
                  apiData = await postData(`/armies/${sanitizedValues.id}`, {
                    body: body,
                  });
                  postNotification(
                    "success",
                    `${gameSystem?.factionLabel || "Faction"} Updated`
                  );
                } else {
                  apiData = await postData(`/armies`, {
                    body: sanitizedValues,
                  });

                  postNotification(
                    "success",
                    `${gameSystem?.factionLabel || "Faction"} Created`
                  );
                }

                apiData.ITCFaction = values.ITCFaction;
                if (newImageFile?.cloudFrontDomain) {
                  apiData.imageFile = {
                    id: newImageFile.fileId,
                    cloudFrontDomain: newImageFile.cloudFrontDomain,
                    filename: newImageFile.fileName,
                  };
                }
                var newFactions;
                if (sanitizedValues.id) {
                  newFactions = [];
                  factions.forEach((currentFaction) => {
                    if (currentFaction.id === sanitizedValues.id) {
                      newFactions.push(apiData);
                    } else if (
                      currentFaction.ITCFactionId === sanitizedValues.id
                    ) {
                      newFactions.push({
                        ...currentFaction,
                        ITCFaction: apiData,
                      });
                    } else {
                      newFactions.push(currentFaction);
                    }
                    newFactions.sort(sortFactions);
                  });
                } else {
                  newFactions = [apiData, ...factions];
                  newFactions.sort(sortFactions);
                }
                setFactions(newFactions);
                setFaction(null);
                setProcessing(false);
                return;
              } catch (err) {
                postNotification("error", err);
                setProcessing(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      name="name"
                      label="Name"
                      value={values.name}
                      variant="outlined"
                      fullWidth
                      required
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        if (!touched.searchNameList && !faction?.id) {
                          let searchName = e.target.value.toLowerCase();

                          setFieldValue("searchName", [searchName], false);
                          setFieldValue(
                            "searchNameList",
                            e.target.value,
                            false
                          );
                        }
                      }}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Search Names"
                      name="searchNameList"
                      value={values.searchNameList}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      inputProps={{ style: { textTransform: "lowercase" } }}
                      onBlur={(e) => {
                        handleBlur(e);
                      }}
                      onChange={(e) => {
                        handleChange(e);
                        var searchNames = e.target.value
                          ? e.target.value.split(",")
                          : [];
                        searchNames = searchNames.map((searchName) =>
                          searchName.toLowerCase().trim()
                        );
                        setFieldValue(
                          "searchName",
                          searchNames.filter((el) => el !== "")
                        );
                      }}
                      error={Boolean(
                        touched.searchNameList && errors.searchNameList
                      )}
                      helperText={
                        touched.searchNameList && errors.searchNameList
                      }
                    />
                    {values.searchName?.length > 0 &&
                      values.searchName.map((searchName, index) => {
                        return (
                          <Chip
                            key={index}
                            label={searchName?.toLowerCase()}
                            color={
                              theme?.palette?.mode === "dark"
                                ? "secondary"
                                : "primary"
                            }
                            variant="outlined"
                          />
                        );
                      })}
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Active"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.active}
                          onChange={(e) => {
                            setFieldValue("active", !values.active);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label={`Circuit ${gameSystem?.factionLabel || "Faction"}`}
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.leagueFaction}
                          onChange={(e) => {
                            setFieldValue(
                              "leagueFaction",
                              !values.leagueFaction
                            );
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label={`Parent ${gameSystem?.factionLabel || "Faction"}`}
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.itcFaction}
                          onChange={(e) => {
                            setFieldValue("itcFaction", !values.itcFaction);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  {!values?.itcFaction && (
                    <Grid item xs={12}>
                      <GenericPicker
                        name="ITCFactionId"
                        label={`Parent ${
                          gameSystem?.factionLabel || "Faction"
                        }`}
                        value={values.ITCFactionId}
                        handleChange={(e) => {
                          handleChange(e);
                          setFieldValue(
                            "ITCFaction",
                            e.target.value && e.target.value !== ""
                              ? factions.filter(
                                  (el) => el.id === e.target.value
                                )[0]
                              : undefined
                          );
                        }}
                        list={
                          factions
                            ? factions
                                .filter((el) => el.ITCFactionId === el.id)
                                .map((el) => {
                                  return {
                                    label: el.name,
                                    value: el.id,
                                  };
                                })
                            : []
                        }
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      name="rankingSortOrder"
                      label="Ranking Sort Order"
                      value={values.rankingSortOrder}
                      variant="outlined"
                      fullWidth
                      type="number"
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(
                        touched.rankingSortOrder && errors.rankingSortOrder
                      )}
                      helperText={
                        touched.rankingSortOrder && errors.rankingSortOrder
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body1">Image</Typography>
                    <ImageUploader
                      values={values}
                      label="Image"
                      name="factionImage"
                      fileId={values.imageFileId}
                      setFieldValue={setFieldValue}
                      defaultUrl={
                        faction?.imageFile?.cloudFrontDomain
                          ? `${faction?.imageFile?.cloudFrontDomain}${faction?.imageFile?.filename}`
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setFaction(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                      disabled={!values.name}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      ) : (
        <></>
      )}
    </Modal>
  );
}
